import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, RotateCcw, X, ZoomIn, ZoomOut } from "lucide-react";
import "./PhotoCropper.css";

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const OUTPUT_WIDTH = 1200;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

/**
 * Crop a listing photo before it is uploaded.
 *
 * The public photo well is a wide landscape box (~1.9:1). A portrait taken on a
 * phone dropped straight into it loses most of its height, which is how a face
 * ends up cut off. This lets the owner choose the part that matters, and what
 * gets uploaded is that crop rather than the original.
 *
 * Direct manipulation throughout: drag to move, pinch to zoom, wheel to zoom
 * about the cursor, double-tap to toggle. The slider is a fallback for a mouse
 * without a wheel, not the main control — on a phone the fingers are.
 *
 * @param {string} src        Data URL, or an https URL for a photo already saved.
 * @param {number} aspect     Target width/height, matching the public well.
 * @param {Function} onCancel Dismiss without changing the photo.
 * @param {Function} onCrop   Receives the cropped image as a JPEG data URL.
 */
export default function PhotoCropper({ src, aspect = 1.9, onCancel, onCrop }) {
  const frameRef = useRef(null);
  const imgRef = useRef(null);

  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [natural, setNatural] = useState(null);
  const [frame, setFrame] = useState({ w: 0, h: 0 });
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Live pointers, so one finger pans and two pinch.
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef(0);

  // Measure the frame rather than trusting the stylesheet: everything below is
  // in real pixels, and the frame is fluid.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setFrame({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Escape closes, and the page behind must not scroll while this is open.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onCancel();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onCancel]);

  /** Scale at which the image exactly covers the frame — this is zoom 1. */
  const baseScale = useCallback(() => {
    if (!natural || !frame.w || !frame.h) return 1;
    return Math.max(frame.w / natural.w, frame.h / natural.h);
  }, [natural, frame]);

  /** The image may never be dragged far enough to expose an edge. */
  const clampOffset = useCallback(
    (next, z) => {
      if (!natural || !frame.w) return next;
      const s = baseScale() * z;
      const maxX = Math.max(0, (natural.w * s - frame.w) / 2);
      const maxY = Math.max(0, (natural.h * s - frame.h) / 2);
      return { x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) };
    },
    [natural, frame, baseScale],
  );

  /**
   * Zoom while holding one point of the image still under `anchor`, which is
   * what makes pinch and wheel feel attached to the photo rather than to the
   * middle of the box.
   */
  const zoomAbout = useCallback(
    (nextZoom, anchor, from = { zoom, offset }) => {
      const z1 = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
      if (!frame.w) return { zoom: z1, offset: from.offset };
      const c = { x: frame.w / 2, y: frame.h / 2 };
      const ratio = z1 / from.zoom;
      const next = {
        x: anchor.x - c.x - ratio * (anchor.x - c.x - from.offset.x),
        y: anchor.y - c.y - ratio * (anchor.y - c.y - from.offset.y),
      };
      return { zoom: z1, offset: clampOffset(next, z1) };
    },
    [frame, zoom, offset, clampOffset],
  );

  const applyZoom = (nextZoom, anchor) => {
    const a = anchor || { x: frame.w / 2, y: frame.h / 2 };
    const r = zoomAbout(nextZoom, a);
    setZoom(r.zoom);
    setOffset(r.offset);
  };

  // ── Gestures ──────────────────────────────────────────────────────────────
  const localPoint = (e) => {
    const r = frameRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  /**
   * Capture is best-effort. It throws for a pointer id the element does not
   * own — a synthetic event, or a pointer already released — and losing the
   * whole gesture over that is worse than dragging without capture.
   */
  const capture = (id) => {
    try {
      frameRef.current?.setPointerCapture?.(id);
    } catch (err) {
      /* no capture; the gesture still tracks through the move handler */
    }
  };

  const release = (id) => {
    try {
      frameRef.current?.releasePointerCapture?.(id);
    } catch (err) {
      /* already released */
    }
  };

  const onPointerDown = (e) => {
    // State first, capture second: capture can throw, and the gesture must
    // still be registered if it does.
    pointers.current.set(e.pointerId, localPoint(e));
    capture(e.pointerId);

    if (pointers.current.size === 1) {
      // Double-tap / double-click toggles between fit and 2x, at that point.
      const now = Date.now();
      if (now - lastTap.current < 300) {
        const p = localPoint(e);
        applyZoom(zoom > 1.05 ? MIN_ZOOM : 2, p);
        lastTap.current = 0;
        gesture.current = null;
        return;
      }
      lastTap.current = now;
      gesture.current = { type: "pan", start: localPoint(e), offset };
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = {
        type: "pinch",
        dist: distance(a, b),
        mid: midpoint(a, b),
        zoom,
        offset,
      };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, localPoint(e));
    const g = gesture.current;
    if (!g) return;

    if (g.type === "pan" && pointers.current.size === 1) {
      const p = localPoint(e);
      setOffset(
        clampOffset(
          { x: g.offset.x + (p.x - g.start.x), y: g.offset.y + (p.y - g.start.y) },
          zoom,
        ),
      );
      return;
    }

    if (g.type === "pinch" && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const d = distance(a, b);
      if (!g.dist) return;
      const mid = midpoint(a, b);
      // Zoom from the pinch, then carry the pan of the midpoint itself, so two
      // fingers can scale and move at the same time.
      const r = zoomAbout(g.zoom * (d / g.dist), g.mid, {
        zoom: g.zoom,
        offset: g.offset,
      });
      setZoom(r.zoom);
      setOffset(
        clampOffset(
          { x: r.offset.x + (mid.x - g.mid.x), y: r.offset.y + (mid.y - g.mid.y) },
          r.zoom,
        ),
      );
    }
  };

  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    release(e.pointerId);
    if (pointers.current.size === 1) {
      // Coming out of a pinch with one finger down — restart the pan cleanly.
      const [p] = [...pointers.current.values()];
      gesture.current = { type: "pan", start: p, offset };
    } else if (pointers.current.size === 0) {
      gesture.current = null;
    }
  };

  const onWheel = (e) => {
    e.preventDefault();
    applyZoom(zoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12), localPoint(e));
  };

  const onKeyDown = (e) => {
    const step = e.shiftKey ? 24 : 8;
    const moves = {
      ArrowLeft: { x: -step, y: 0 },
      ArrowRight: { x: step, y: 0 },
      ArrowUp: { x: 0, y: -step },
      ArrowDown: { x: 0, y: step },
    };
    if (moves[e.key]) {
      e.preventDefault();
      setOffset((o) => clampOffset({ x: o.x + moves[e.key].x, y: o.y + moves[e.key].y }, zoom));
    } else if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      applyZoom(zoom + 0.2);
    } else if (e.key === "-" || e.key === "_") {
      e.preventDefault();
      applyZoom(zoom - 0.2);
    }
  };

  const reset = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const handleLoad = (e) => {
    const el = e.currentTarget;
    setNatural({ w: el.naturalWidth, h: el.naturalHeight });
    reset();
  };

  /** Render exactly what the frame is showing to a canvas. */
  const confirm = async () => {
    const img = imgRef.current;
    if (!img || !natural || !frame.w) return;
    setBusy(true);
    try {
      const s = baseScale() * zoom;
      const left = frame.w / 2 - (natural.w * s) / 2 + offset.x;
      const top = frame.h / 2 - (natural.h * s) / 2 + offset.y;
      const sx = clamp(-left / s, 0, natural.w);
      const sy = clamp(-top / s, 0, natural.h);
      const sw = Math.min(natural.w - sx, frame.w / s);
      const sh = Math.min(natural.h - sy, frame.h / s);

      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_WIDTH;
      canvas.height = Math.round(OUTPUT_WIDTH / (frame.w / frame.h));
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      onCrop(canvas.toDataURL("image/jpeg", 0.9));
    } catch (err) {
      // A photo already on the listing is served from S3; if it is not
      // CORS-readable the canvas is tainted and cannot be exported.
      setFailed(true);
      setBusy(false);
    }
  };

  const s = baseScale() * zoom;
  const imgStyle = natural
    ? {
        width: `${natural.w * s}px`,
        height: `${natural.h * s}px`,
        transform: `translate3d(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px), 0)`,
      }
    : { opacity: 0 };

  return (
    <div
      className="pc-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="pc-sheet" role="dialog" aria-modal="true" aria-label="Crop your listing photo">
        <header className="pc-head">
          <button type="button" className="pc-icon-btn" onClick={onCancel} aria-label="Cancel">
            <X size={20} />
          </button>
          <h2 className="pc-title">Crop photo</h2>
          <button
            type="button"
            className="pc-icon-btn"
            onClick={reset}
            aria-label="Reset crop"
            title="Reset"
          >
            <RotateCcw size={18} />
          </button>
        </header>

        <div className="pc-stage">
          <div
            className="pc-frame"
            style={{ aspectRatio: String(aspect) }}
            ref={frameRef}
            tabIndex={0}
            role="application"
            aria-label="Crop area. Drag to move, pinch or scroll to zoom, arrow keys to nudge."
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            onWheel={onWheel}
            onKeyDown={onKeyDown}
            onDoubleClick={(e) => e.preventDefault()}
          >
            <img
              ref={imgRef}
              src={src}
              alt=""
              className="pc-img"
              style={imgStyle}
              onLoad={handleLoad}
              onError={() => setFailed(true)}
              draggable={false}
              crossOrigin="anonymous"
            />
            {/* Thirds, to line a face up against. Purely decorative. */}
            <div className="pc-grid" aria-hidden="true">
              <span /><span /><span /><span />
            </div>
          </div>
          <p className="pc-hint">
            Drag to move · pinch or scroll to zoom · double-tap to fill
          </p>
        </div>

        {failed && (
          <p className="pc-error" role="alert">
            That photo could not be opened for cropping. Choose the file again
            and crop it before saving.
          </p>
        )}

        <div className="pc-zoom">
          <button
            type="button"
            className="pc-icon-btn"
            onClick={() => applyZoom(zoom - 0.25)}
            aria-label="Zoom out"
            disabled={zoom <= MIN_ZOOM}
          >
            <ZoomOut size={18} />
          </button>
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step="0.01"
            value={zoom}
            onChange={(e) => applyZoom(Number(e.target.value))}
            aria-label="Zoom"
            className="pc-range"
          />
          <button
            type="button"
            className="pc-icon-btn"
            onClick={() => applyZoom(zoom + 0.25)}
            aria-label="Zoom in"
            disabled={zoom >= MAX_ZOOM}
          >
            <ZoomIn size={18} />
          </button>
        </div>

        <div className="pc-actions">
          <button type="button" className="pc-btn pc-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="pc-btn pc-btn-primary"
            onClick={confirm}
            disabled={!natural || busy || failed}
          >
            <Check size={17} aria-hidden="true" /> {busy ? "Applying…" : "Apply crop"}
          </button>
        </div>
      </div>
    </div>
  );
}
